import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createCheckout } from '../services/api';
import api from '../services/api';
import { Bot, Package, CalendarDays, BarChart3 } from 'lucide-react';
import AuthGridBg from '../components/AuthGridBg';

type Step = 'form' | 'verify' | 'payment';

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  storeName: string;
  storePhone: string;
}

// ── Indicador de pasos ────────────────────────────────────────────────────
function StepBar({ step }: { step: Step }) {
  const steps = [
    { key: 'form',    label: 'Crear cuenta' },
    { key: 'verify',  label: 'Verificar email' },
    { key: 'payment', label: 'Pago' },
  ];
  const activeIdx = steps.findIndex(s => s.key === step);
  return (
    <div className="flex items-center gap-2 mb-8">
      {steps.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={s.key} className="flex items-center gap-2 flex-1 last:flex-none">
            <div className="flex items-center gap-2 text-sm font-medium whitespace-nowrap">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  background: active ? '#D4FF00' : done ? 'rgba(212,255,0,0.2)' : 'rgba(255,255,255,0.06)',
                  color: active ? '#0A0A0F' : done ? '#D4FF00' : 'rgba(255,255,255,0.4)',
                }}
              >
                {done ? '✓' : i + 1}
              </div>
              <span
                className="hidden sm:inline text-sm"
                style={{ color: active ? '#D4FF00' : done ? 'rgba(212,255,0,0.6)' : 'rgba(255,255,255,0.3)' }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className="flex-1 h-px mx-1"
                style={{ background: i < activeIdx ? 'rgba(212,255,0,0.4)' : 'rgba(255,255,255,0.06)' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Panel izquierdo ───────────────────────────────────────────────────────
function LeftPanel() {
  return (
    <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative z-10 border-r border-white/5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#D4FF00' }}>
          <span className="font-black text-base" style={{ color: '#0A0A0F' }}>S</span>
        </div>
        <span className="text-white font-bold text-lg">Stockup Messages</span>
      </div>

      <div>
        <h2 className="text-4xl font-bold text-white leading-tight mb-4">
          Empieza a vender<br/>más con WhatsApp
        </h2>
        <p className="text-white/50 text-lg">
          CRM con IA. Automatiza respuestas, gestiona pedidos, agenda citas y crece tu negocio.
        </p>
        <div className="mt-10 space-y-3">
          {[
            { Icon: Bot,          text: 'IA responde a tus clientes 24/7' },
            { Icon: Package,      text: 'Gestión de productos, servicios y órdenes' },
            { Icon: CalendarDays, text: 'Agendamiento automático de citas' },
            { Icon: BarChart3,    text: 'Analíticas y campañas de WhatsApp' },
          ].map(f => (
            <div
              key={f.text}
              className="flex items-center gap-3 rounded-xl px-4 py-3"
              style={{ background: 'rgba(212,255,0,0.04)', border: '1px solid rgba(212,255,0,0.1)' }}
            >
              <f.Icon size={18} style={{ color: '#D4FF00' }} strokeWidth={1.5} className="flex-shrink-0" />
              <span className="text-white/70 text-sm">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-white/25 text-sm">&copy; 2026 Stockup Messages</p>
    </div>
  );
}

// ── Input helper ──────────────────────────────────────────────────────────
function DarkInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full px-3.5 py-2.5 rounded-xl text-sm outline-none transition-all"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: 'white',
        ...(props.style ?? {}),
      }}
      onFocus={e => { e.target.style.borderColor = 'rgba(212,255,0,0.45)'; props.onFocus?.(e); }}
      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; props.onBlur?.(e); }}
    />
  );
}

export default function Register() {
  const [step, setStep]           = useState<Step>('form');
  const [sessionId, setSessionId] = useState('');
  const [form, setForm]           = useState<FormData>({ name: '', email: '', password: '', confirmPassword: '', storeName: '', storePhone: '' });
  const [code, setCode]           = useState(['', '', '', '', '', '']);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [termsAccepted, setTermsAccepted]   = useState(false);
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Las contraseñas no coinciden'); return; }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return; }
    if (!form.storeName.trim() || !form.storePhone.trim()) { setError('Completa el nombre y teléfono de tu negocio'); return; }
    if (!termsAccepted) { setError('Debes aceptar los Términos y Condiciones y la Política de Privacidad para continuar'); return; }

    setLoading(true);
    try {
      const res = await api.post('/auth/send-verification', {
        name: form.name, email: form.email, password: form.password,
        storeName: form.storeName, storePhone: form.storePhone,
      });
      setSessionId(res.data.sessionId);
      setStep('verify');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Error al enviar el código. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const fullCode = code.join('');
    if (fullCode.length < 6) { setError('Ingresa el código de 6 dígitos'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-and-register', { sessionId, code: fullCode });
      const { access_token, userId, email, role, storeId } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify({ userId, email, role, storeId }));
      setStep('payment');
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Código incorrecto o expirado.');
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      const res = await api.post('/auth/send-verification', {
        name: form.name, email: form.email, password: form.password,
        storeName: form.storeName, storePhone: form.storePhone,
      });
      setSessionId(res.data.sessionId);
      setCode(['', '', '', '', '', '']);
      setResendCooldown(60);
    } catch { setError('Error al reenviar el código.'); }
  };

  const handleCodeInput = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[idx] = digit;
    setCode(next);
    if (digit && idx < 5) codeRefs.current[idx + 1]?.focus();
  };

  const handleCodeKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) codeRefs.current[idx - 1]?.focus();
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { setCode(pasted.split('')); codeRefs.current[5]?.focus(); }
  };

  const handlePay = async () => {
    setPayLoading(true);
    setError('');
    try {
      const res = await createCheckout();
      window.location.href = res.data.initPoint;
    } catch {
      setError('Error al iniciar el pago. Intenta de nuevo.');
      setPayLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden" style={{ background: '#0A0A0F' }}>
      <AuthGridBg />
      <LeftPanel />

      {/* Panel derecho */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="lg:hidden flex items-center gap-2.5 justify-center mb-6">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#D4FF00' }}>
              <span className="font-black text-xs" style={{ color: '#0A0A0F' }}>S</span>
            </div>
            <span className="text-white font-bold text-base">Stockup Messages</span>
          </div>

          <div className="rounded-2xl border p-7" style={{ background: 'rgba(17,17,23,0.85)', borderColor: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)' }}>
            <StepBar step={step} />

            {/* ── PASO 1: Formulario ── */}
            {step === 'form' && (
              <>
                <h1 className="text-2xl font-bold text-white mb-1">Crear cuenta</h1>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  ¿Ya tienes cuenta?{' '}
                  <Link to="/login" className="font-medium hover:underline" style={{ color: '#D4FF00' }}>Inicia sesión</Link>
                </p>

                {error && (
                  <div className="mb-4 p-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSendCode} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Tu nombre</label>
                      <DarkInput name="name" value={form.name} onChange={handleChange} required placeholder="Alex Gómez" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Email</label>
                      <DarkInput name="email" type="email" value={form.email} onChange={handleChange} required placeholder="tu@email.com" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Contraseña</label>
                      <DarkInput name="password" type="password" value={form.password} onChange={handleChange} required placeholder="Mín. 8 caracteres" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Confirmar</label>
                      <DarkInput name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} required placeholder="Repetir" />
                    </div>
                  </div>

                  <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'rgba(255,255,255,0.3)' }}>Tu negocio</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Nombre del negocio</label>
                        <DarkInput name="storeName" value={form.storeName} onChange={handleChange} required placeholder="Mi Tienda" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>WhatsApp del negocio</label>
                        <DarkInput name="storePhone" value={form.storePhone} onChange={handleChange} required placeholder="573001234567" />
                      </div>
                    </div>
                  </div>

                  {/* Checkbox términos */}
                  <label className="flex items-start gap-3 cursor-pointer group mt-1">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="sr-only" />
                      <div
                        onClick={() => setTermsAccepted(v => !v)}
                        className="w-5 h-5 rounded-md flex items-center justify-center transition-all"
                        style={{
                          background: termsAccepted ? '#D4FF00' : 'rgba(255,255,255,0.04)',
                          border: `2px solid ${termsAccepted ? '#D4FF00' : 'rgba(255,255,255,0.15)'}`,
                        }}
                      >
                        {termsAccepted && (
                          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                            <path d="M1 4.5L4 7.5L10 1" stroke="#0A0A0F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-xs leading-relaxed"
                      style={{ color: 'rgba(255,255,255,0.45)' }}
                      onClick={() => setTermsAccepted(v => !v)}
                    >
                      He leído y acepto los{' '}
                      <Link to="/terminos" target="_blank" className="hover:underline font-medium" style={{ color: '#D4FF00' }} onClick={e => e.stopPropagation()}>
                        Términos y Condiciones
                      </Link>
                      {' '}y la{' '}
                      <Link to="/privacidad" target="_blank" className="hover:underline font-medium" style={{ color: '#D4FF00' }} onClick={e => e.stopPropagation()}>
                        Política de Privacidad
                      </Link>
                      , incluyendo el tratamiento de mis datos personales conforme a la Ley 1581 de 2012.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading || !termsAccepted}
                    className="w-full py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 mt-1"
                    style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)', color: '#0A0A0F' }}
                  >
                    {loading ? 'Enviando código...' : 'Continuar — verificar email'}
                  </button>
                </form>
              </>
            )}

            {/* ── PASO 2: Verificar email ── */}
            {step === 'verify' && (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(212,255,0,0.08)', border: '1px solid rgba(212,255,0,0.2)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4FF00" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <h1 className="text-2xl font-bold text-white mb-1">Verifica tu email</h1>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    Enviamos un código de 6 dígitos a<br/>
                    <strong className="text-white">{form.email}</strong>
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 rounded-xl text-sm text-center" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleVerify}>
                  <div className="flex gap-2 justify-center mb-6" onPaste={handleCodePaste}>
                    {code.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={el => { codeRefs.current[idx] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleCodeInput(idx, e.target.value)}
                        onKeyDown={e => handleCodeKeyDown(idx, e)}
                        className="w-11 h-14 text-center text-xl font-bold rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: `2px solid ${digit ? '#D4FF00' : 'rgba(255,255,255,0.1)'}`,
                          color: 'white',
                        }}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || code.join('').length < 6}
                    className="w-full py-3.5 rounded-xl font-semibold text-sm transition disabled:opacity-40 mb-4"
                    style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)', color: '#0A0A0F' }}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Verificando...
                      </span>
                    ) : 'Verificar y crear cuenta'}
                  </button>
                </form>

                <div className="text-center space-y-2">
                  <button
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    className="text-sm hover:underline transition disabled:no-underline"
                    style={{ color: resendCooldown > 0 ? 'rgba(255,255,255,0.25)' : '#D4FF00' }}
                  >
                    {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código'}
                  </button>
                  <br/>
                  <button
                    onClick={() => { setStep('form'); setCode(['','','','','','']); setError(''); }}
                    className="text-sm transition hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.3)' }}
                  >
                    Cambiar email
                  </button>
                </div>
              </>
            )}

            {/* ── PASO 3: Pago ── */}
            {step === 'payment' && (
              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="#0A0A0F" strokeWidth="2.5" strokeLinecap="round"/>
                    <polyline points="22 4 12 14.01 9 11.01" stroke="#0A0A0F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>

                <h1 className="text-2xl font-bold text-white mb-1">¡Email verificado!</h1>
                <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Tu cuenta fue creada. Activa tu suscripción para empezar.
                </p>

                <div className="rounded-2xl border p-5 mb-6 text-left" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full" style={{ background: 'rgba(212,255,0,0.1)', color: '#D4FF00' }}>
                        Plan mensual
                      </span>
                      <h3 className="text-lg font-bold text-white mt-2">Stockup Messages</h3>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-white">$24.000</div>
                      <div className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>COP / mes</div>
                    </div>
                  </div>
                  <div className="space-y-2 border-t pt-4" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    {['IA responde a tus clientes en WhatsApp', 'Gestión de productos, servicios y órdenes', 'Agendamiento automático de citas', 'Campañas masivas de WhatsApp', 'Panel de analíticas'].map(f => (
                      <div key={f} className="flex items-center gap-2.5 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                          <path d="M20 6L9 17l-5-5" stroke="#D4FF00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {f}
                      </div>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                    {error}
                  </div>
                )}

                <button
                  onClick={handlePay}
                  disabled={payLoading}
                  className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition disabled:opacity-60 mb-3 flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #009ee3, #003087)' }}
                >
                  {payLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Redirigiendo a MercadoPago...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="5" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
                        <path d="M2 10h20" stroke="white" strokeWidth="2"/>
                      </svg>
                      Pagar con MercadoPago
                    </>
                  )}
                </button>
                <button
                  onClick={() => window.location.href = '/subscription'}
                  className="w-full py-2.5 rounded-xl text-sm transition hover:text-white"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                >
                  Pagar después
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
